import { useEffect, useMemo, useState } from "react";
import type { Backtest, OrderStats } from "@/api/types";
import { useApi } from "@/context/ApiContext";
import { isLiveInstance } from "@/lib/live";

export type RealPnl = {
  pnl: number;
  trades: number;
  /** Share of closed trades that made money, 0..1. */
  winRate: number;
};

/** Key of one live strategy on one account in what `useLiveRealPnl` returns. */
export const realPnlKey = (backtestId: string, accountId: string | null | undefined) => `${backtestId}:${accountId || ""}`;

/**
 * Realized P&L of each live strategy on each account it trades on, from the
 * orders it actually filled there (`account.orders_stats`), keyed by
 * `realPnlKey`. A strategy on two exchanges has a result on each. Missing until loaded.
 */
export function useLiveRealPnl(backtests: Backtest[]): Record<string, RealPnl> {
  const api = useApi([]);
  const [byId, setById] = useState<Record<string, RealPnl>>({});
  const targets = useMemo(
    () =>
      backtests
        .filter(isLiveInstance)
        .flatMap((bt) => (bt.accounts || []).map((a) => ({ id: bt.id, accountId: a.account_id || "" })))
        .filter((t) => t.accountId),
    [backtests]
  );
  const key = targets.map((t) => `${t.id}:${t.accountId}`).join(",");

  useEffect(() => {
    if (!api.ready || !targets.length) return;
    let cancelled = false;
    void Promise.all(
      targets.map(async (t) => {
        try {
          const s = (await api.commands.getOrderStats({ account_id: t.accountId, backtest_id: t.id })) as OrderStats | null;
          const pnl = Number(s?.trading?.realized_pnl);
          const winRate = Number(s?.trading?.win_rate);
          return [
            realPnlKey(t.id, t.accountId),
            { pnl: Number.isFinite(pnl) ? pnl : 0, trades: s?.trading?.trades ?? 0, winRate: Number.isFinite(winRate) ? winRate : 0 },
          ] as const;
        } catch {
          return null;
        }
      })
    ).then((rows) => {
      if (cancelled) return;
      const next: Record<string, RealPnl> = {};
      for (const r of rows) if (r) next[r[0]] = r[1];
      setById(next);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.ready, key]);

  return byId;
}

/** Sum per-strategy (and account) results; null until anything has loaded. */
export function sumRealPnl(byId: Record<string, RealPnl>): RealPnl | null {
  const rows = Object.values(byId);
  if (!rows.length) return null;
  const trades = rows.reduce((sum, r) => sum + r.trades, 0);
  return {
    pnl: rows.reduce((sum, r) => sum + r.pnl, 0),
    trades,
    // Weighted by each strategy's trade count.
    winRate: trades ? rows.reduce((sum, r) => sum + r.winRate * r.trades, 0) / trades : 0,
  };
}
